import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import ToolManagement from './ToolManagement';
import SharedTable from '../shared/Table/SharedTable';
import UserEditModal from './components/UserEditModal';
import StatusBadge from '../shared/StatusBadge/StatusBadge';
import './AdminPanel.css';

/**
 * JSDoc Header: AdminPanel Component
 * 
 * Main orchestrator for the Admin section. Shows user administration list,
 * stats bar for license limits, role filter dropdown, and integrates the tool management.
 * Uses SharedTable for rendering users and UserEditModal for editing/creation.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onOpenJournalProject - Callback to open the journal of a specific project
 */
export default function AdminPanel({ onOpenJournalProject }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Tab selection from URL path
  const getTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/admin/tools')) return 'tools';
    return 'users';
  };

  const [activeTab, setActiveTab] = useState(getTabFromPath());

  useEffect(() => {
    setActiveTab(getTabFromPath());
  }, [location.pathname]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/admin/${tab}`);
  };

  // Data States
  const [users, setUsers] = useState([]);
  const [companyTools, setCompanyTools] = useState([]);
  const [stats, setStats] = useState({
    current_users: 0,
    max_users: 0,
    current_total_users: 0,
    max_total_users: 0
  });

  // UI States
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(['admin', 'manager', 'employee']);
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const roleFilterRef = useRef(null);

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isToolActive, setIsToolActive] = useState(false);

  const token = localStorage.getItem('token');

  const getCurrentUserId = () => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch (e) {
      return null;
    }
  };
  const currentUserId = getCurrentUserId();

  // Data Fetching
  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await adminApi.getUsers();
      setUsers(usersData);

      const statsData = await adminApi.getCompanyStats();
      setStats(statsData);
    } catch (err) {
      console.error("Fehler beim Laden:", err);
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Fehler beim Laden der Daten');
      }
    }
  }, [navigate]);

  const fetchCompanyTools = useCallback(async () => {
    try {
      const data = await adminApi.getCompanyTools();
      setCompanyTools(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchCompanyTools();
    }
  }, [token, fetchUsers, fetchCompanyTools]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (roleFilterRef.current && !roleFilterRef.current.contains(event.target)) {
        setIsRoleFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter & Search Logic
  const uniqueRoles = [...new Set(users.map(u => u.role))].filter(Boolean).sort();

  const getProcessedUsers = () => {
    return users.filter(user => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        user.name.toLowerCase().includes(term) ||
        user.username.toLowerCase().includes(term);
      const matchesRole = roleFilter.includes(user.role);
      return matchesSearch && matchesRole;
    });
  };

  const finalUsers = getProcessedUsers();

  // Modal Actions
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const openCreateModal = () => {
    if (stats.max_total_users !== -1 && stats.current_total_users >= stats.max_total_users) {
      alert(`⛔ Account-Limit erreicht! Dein Paket erlaubt maximal ${stats.max_total_users} gespeicherte Nutzer (aktiv + inaktiv).\nBitte lösche alte Nutzer endgültig, um Platz zu schaffen, oder buche ein größeres Paket.`);
      return;
    }

    if (stats.max_users !== -1 && stats.current_users >= stats.max_users) {
      const proceed = window.confirm(`⚠️ Lizenz-Limit erreicht (${stats.max_users} aktive Nutzer).\nDu kannst nur einen INAKTIVEN Nutzer anlegen.\nFortfahren?`);
      if (!proceed) return;
    }

    setSelectedUser(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const toggleRoleFilter = (role) => {
    setRoleFilter(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleAllRoles = () => setRoleFilter(uniqueRoles);
  const handleNoRoles = () => setRoleFilter([]);

  const getRoleFilterLabel = () => {
    const allUsedSelected = uniqueRoles.length > 0 && uniqueRoles.every(role => roleFilter.includes(role));
    if (allUsedSelected) return 'Alle Rollen';
    if (roleFilter.length === 0) return 'Keine Auswahl';

    const selectedUsedRoles = roleFilter.filter(role => uniqueRoles.includes(role));
    if (selectedUsedRoles.length === 1) {
      const labels = { admin: 'Admin', manager: 'Manager', employee: 'Mitarbeiter' };
      return labels[selectedUsedRoles[0]] || selectedUsedRoles[0];
    }
    return `${selectedUsedRoles.length} Rollen`;
  };

  // Definition of actions for SharedTable (ActionMenu)
  const tableActions = (user) => {
    const isLocked = user.is_primary_admin && user.id !== currentUserId;
    if (isLocked) return [];

    return [
      {
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        ),
        label: 'Bearbeiten',
        onClick: () => openEditModal(user),
        tooltip: 'Bearbeiten'
      }
    ];
  };

  // SharedTable columns configuration
  const columns = [
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      className: 'align-center admin-col-status',
      render: (user) => <StatusBadge status={user.is_active !== false ? 'aktiv' : 'inaktiv'} />
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (user) => <span style={{ fontWeight: '500' }}>{user.name}</span>
    },
    {
      key: 'username',
      label: 'Benutzername',
      sortable: true,
      className: 'admin-col-username'
    },
    {
      key: 'role',
      label: 'Systemberechtigung',
      sortable: true,
      className: 'align-center admin-col-role',
      render: (user) => (
        <span className={`role-badge ${user.role}`}>
          {user.role === 'employee' ? 'Mitarbeiter' : user.role}
        </span>
      )
    }
  ];

  return (
    <div className="admin-panel">
      {/* HEADER */}
      <div className="admin-header">
        {!isToolActive && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {activeTab === 'users' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
                  {/* 1. BALKEN: Aktive Lizenzen */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '600', color: '#444' }}>
                      <span>Aktive Lizenzen</span>
                      <span>{stats.current_users} / {stats.max_users === -1 ? '∞' : stats.max_users}</span>
                    </div>
                    <div style={{ width: '100%', height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: stats.max_users === -1 ? '0%' : `${Math.min((stats.current_users / stats.max_users) * 100, 100)}%`,
                        height: '100%',
                        background: stats.max_users !== -1 && stats.current_users >= stats.max_users ? '#ef4444' : '#10b981',
                        transition: 'width 0.5s ease-out'
                      }}></div>
                    </div>
                  </div>

                  {/* 2. BALKEN: Gesamt Accounts */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '600', color: '#666' }}>
                      <span>Gesamtnutzer</span>
                      <span>{stats.current_total_users || 0} / {(!stats.max_total_users || stats.max_total_users === -1) ? '∞' : stats.max_total_users}</span>
                    </div>
                    <div style={{ width: '100%', height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: (!stats.max_total_users || stats.max_total_users === -1) ? '0%' : `${Math.min((stats.current_total_users / stats.max_total_users) * 100, 100)}%`,
                        height: '100%',
                        background: stats.max_total_users !== -1 && stats.current_total_users >= stats.max_total_users ? '#f59e0b' : '#3b82f6',
                        transition: 'width 0.5s ease-out'
                      }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {!isToolActive && (
          <div className="admin-tabs">
            <button
              className={activeTab === 'users' ? 'active' : ''}
              onClick={() => handleTabChange('users')}
            >
              <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>👥</span>
              <span>Nutzer</span>
            </button>

            <button
              className={activeTab === 'tools' ? 'active' : ''}
              onClick={() => handleTabChange('tools')}
            >
              <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>🛠️</span>
              <span>Tools</span>
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* === USERS TAB === */}
      {activeTab === 'users' && (
        <div className="users-view">
          <div className="action-bar">
            <div className="search-box">
              <input
                type="text"
                className="user-search-input"
                placeholder="Suche..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-dropdown-container" ref={roleFilterRef}>
              <button className="filter-dropdown-btn" onClick={() => setIsRoleFilterOpen(!isRoleFilterOpen)}>
                <span>{getRoleFilterLabel()}</span><span>▼</span>
              </button>
              {isRoleFilterOpen && (
                <div className="filter-dropdown-menu">
                  {uniqueRoles.map(role => (
                    <div key={role} className="filter-option" onClick={() => toggleRoleFilter(role)}>
                      <input type="checkbox" checked={roleFilter.includes(role)} readOnly />
                      <span className="filter-label">
                        {role === 'admin' ? 'Admin' : role === 'manager' ? 'Manager' : role === 'employee' ? 'Mitarbeiter' : role}
                      </span>
                    </div>
                  ))}
                  <div className="filter-actions-row">
                    <small className="filter-action-btn action-all" onClick={handleAllRoles}>Alle</small>
                    <span className="filter-action-divider">|</span>
                    <small className="filter-action-btn action-none" onClick={handleNoRoles}>Keine</small>
                  </div>
                </div>
              )}
            </div>
            <button className="add-user-btn" onClick={openCreateModal}><span>+</span> Neuer Nutzer</button>
          </div>

          <div className="table-container">
            <SharedTable
              data={finalUsers}
              columns={columns}
              uniqueKeyField="id"
              emptyMessage="Keine Nutzer gefunden"
              actions={tableActions}
            />
          </div>
        </div>
      )}

      {/* === TOOLS TAB === */}
      {activeTab === 'tools' && (
        <ToolManagement
          onToolActivate={setIsToolActive}
          onOpenJournalProject={onOpenJournalProject}
        />
      )}

      {/* === MODAL === */}
      <UserEditModal
        isOpen={showModal}
        onClose={handleCloseModal}
        user={selectedUser}
        currentUserId={currentUserId}
        companyTools={companyTools}
        users={users}
        stats={stats}
        onSaveSuccess={fetchUsers}
      />
    </div>
  );
}